import React from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import P from 'apps/Pdf/components/common/P';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';

import { formatAmountValue } from 'helpers/helpers';

import { Flows } from 'pages/Accounts/Detail/enums';
import { Currency } from 'enums';
import { AmountValue } from 'common/interfaces';
import useStyles from './overviewStyles';
import { AccountPlan } from 'stores/account/overviewStore/model/interfaces';
import Message, { MessageVariant } from 'components/Message/Message';

interface Props {
  currency: Currency;
  flow: Flows;
  data: AccountPlan;
  transfers: boolean;
  transfersFromCashOnly: boolean;
  withdrawAll: boolean;
  pdf?: boolean;
}

const GeneralOverview: React.FC<Props> = ({
  currency,
  flow,
  data,
  transfers,
  transfersFromCashOnly,
  withdrawAll,
  pdf,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const ArticleWrapper = pdf ? P : Typography;
  const H2Wrapper = pdf ? H1 : Typography;
  const H3Wrapper = pdf ? H2 : Typography;

  return (
    <>
      {(Object.keys(data.transfers).length > 0 ||
        data.fees?.value > 0 ||
        data.insufficiencyConditions.length > 0 ||
        flow === Flows.WITHDRAWAL ||
        flow === Flows.TRANSFER) && (
        <div>
          {flow === Flows.TRANSFER && data.transferData?.transfers && (
            <>
              <H2Wrapper variant="h2" className={pdf ? undefined : classes.movementsTitle}>
                {t('accountDetail_overview_movementsTitle_target')}
              </H2Wrapper>

              {data.transferData.portfolioDifference && (
                <Message
                  id="accountDetail_transfer_target_diff"
                  message={t('accountDetail_transfer_target_diff')}
                  variant={MessageVariant.INFO}
                  permanent
                />
              )}
              {!Object.keys(data.transferData.transfers).some((k) => k === 'CASH_CASH') && (
                <Message
                  id="accountDetail_transfer_target_cashBalance"
                  message={t('accountDetail_transfer_target_cashBalance')}
                  variant={MessageVariant.INFO}
                  permanent
                />
              )}

              <div className={pdf ? undefined : `${classes.wrapper} ${classes.movements}`}>
                <ul style={{ color: '#000' }}>
                  {Object.keys(data.transferData.transfers).map((transfer) => {
                    return (
                      <React.Fragment key={`transfer_target_${transfer}`}>
                        {data!.transferData!.transfers[transfer].value !== 0 && (
                          <li>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t(`accountDetail_overview_movement_accounts`, {
                                  from: t(`accountDetail_overview_movement_${transfer.split('_')[0]}`),
                                  fromTitle: data.transferData?.accountTitle,
                                  to: t(`accountDetail_overview_movement_${transfer.split('_')[1]}`),
                                  toTitle: data.transferData?.targetAccountTitle,
                                  amount:
                                    data!.transferData!.transfers[transfer].value === -1
                                      ? t('accountDetail_overview_movement_all')
                                      : formatAmountValue(
                                          data!.transferData!.transfers[transfer].value,
                                          data!.transferData!.transfers[transfer].currency,
                                        ),
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          </li>
                        )}
                      </React.Fragment>
                    );
                  })}
                </ul>
              </div>
            </>
          )}

          <H2Wrapper variant="h2" className={pdf ? undefined : classes.movementsTitle}>
            {t(`accountDetail_overview_movementsTitle${flow === Flows.TRANSFER ? '_source' : ''}`)}
          </H2Wrapper>

          <div className={pdf ? undefined : `${classes.wrapper} ${classes.movements}`}>
            {flow === Flows.WITHDRAWAL && (
              <ArticleWrapper>
                {data.withdrawal ? (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(`accountDetail_overview_withdrawal_amount`, {
                        amount: formatAmountValue(data.withdrawal.value, data.withdrawal.currency),
                        interpolation: { escapeValue: false },
                      }),
                    }}
                  />
                ) : (
                  t('accountDetail_overview_withdrawal_all')
                )}
              </ArticleWrapper>
            )}

            <ul style={{ color: '#000' }}>
              {flow === Flows.WITHDRAWAL && transfersFromCashOnly && !withdrawAll && (
                <li>{t('accountDetail_overview_movements_cashOnly')}</li>
              )}
              {Object.keys(data.transfers).map((transfer) => {
                return (
                  <React.Fragment key={`transfer_${transfer}`}>
                    {data!.transfers[transfer].value !== 0 && (
                      <li>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t(`accountDetail_overview_movement`, {
                              from: t(`accountDetail_overview_movement_${transfer.split('_')[0]}`),
                              to: t(`accountDetail_overview_movement_${transfer.split('_')[1]}`),
                              amount:
                                data!.transfers[transfer].value === -1
                                  ? t('accountDetail_overview_movement_all')
                                  : formatAmountValue(
                                      data!.transfers[transfer].value,
                                      data!.transfers[transfer].currency,
                                    ),
                              interpolation: { escapeValue: false },
                            }),
                          }}
                        />
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
              {!transfers && (!withdrawAll || flow === Flows.TRANSFER) && (
                <li>{t(`accountDetail_overview_movements_none_${flow}`)}</li>
              )}
            </ul>

            <ul style={{ color: '#000' }}>
              {data.insufficiencyConditions.map((c) => {
                return (
                  <li key={c}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t(`accountDetail_overview_condition_${c}`, {
                          beforeK1: formatAmountValue(data!.before.K1.value, data!.before.K1.currency),
                          afterK1: formatAmountValue(data!.after.K1.value, data!.after.K1.currency),
                          beforeK2: formatAmountValue(data!.before.K2.value, data!.before.K2.currency),
                          afterK2: formatAmountValue(data!.after.K2.value, data!.after.K2.currency),
                          interpolation: { escapeValue: false },
                        }),
                      }}
                    />
                  </li>
                );
              })}
            </ul>

            {flow === Flows.PLAN && (
              <>
                <H3Wrapper variant="h3" className={pdf ? undefined : classes.proposedSubtitle}>
                  {t('accountDetail_overview_feesTitle')}
                </H3Wrapper>
                <ArticleWrapper>
                  {data.fees?.value > 0 ? (
                    <>
                      {t('accountDetail_overview_fees')}{' '}
                      <strong>{formatAmountValue(data.fees.value, data.fees.currency)}</strong>*
                    </>
                  ) : (
                    t('accountDetail_overview_fees_none')
                  )}
                </ArticleWrapper>
              </>
            )}
            {(Object.values(data.transfers).reduce((prev: number, av: AmountValue) => prev + av.value, 0) > 0 ||
              data.fees?.value > 0) && <ArticleWrapper>{t('accountDetail_overview_movements_info')}</ArticleWrapper>}
          </div>
        </div>
      )}
    </>
  );
};

export default GeneralOverview;
